import BaseHttp from "@/api/httpHelper";
import apiGwClient from "@/api/api.gateway";
import type {
  ITenantRentCreate,
  ITenantSubscriptionCreate,
  IUpdateTenantRent,
  ITenantUpdate,
  ITenantBaseRentUpdate,
} from "@/types/requests";
import type {
  ITenantListResponse,
  ITenantDetailResponse,
  ITenantRentListResponse,
} from "@/types/response";

class TenantService extends BaseHttp {
  async addTenantRent(body: ITenantRentCreate) {
    const { data, success, error } = await this.post<string>(
      `/${body.tenantId}/rents/pay`,
      body,
      {
        requestKey: "addTenantRent",
      }
    );
    return { data, success, error };
  }

  async addTenantSubscription(body: ITenantSubscriptionCreate) {
    const { data, success, error } = await this.post<string>(
      `/${body.tenantId}/subscriptions/pay`,
      body,
      {
        requestKey: "addTenantSubscription",
      }
    );
    return { data, success, error };
  }

  async getTenants(
    pageNumber: number,
    pageSize: number,
    searchTerm: string,
    hasFormerDebt = false
  ) {
    const { data } = await this.get<ITenantListResponse>(
      "",
      { pageNumber, pageSize, searchTerm, hasFormerDebt },
      { requestKey: "getTenants" }
    );
    return data;
  }

  async getTenantById(id: string) {
    const { data } = await this.get<ITenantDetailResponse>(
      `/${id}`,
      {},
      { requestKey: "getTenantById" }
    );
    return data;
  }

  async deleteTenant(id: string) {
    const { success, error } = await this.delete<string>(
      `/${id}`,
      {},
      { requestKey: "deleteTenant" }
    );
    return { success, error };
  }

  async updateTenantRent(id: string, rentId: string, body: IUpdateTenantRent) {
    const { data, success, error } = await this.put<string>(
      `/${id}/monthly/${rentId}`,
      body,
      {
        requestKey: "updateTenantRent",
      }
    );
    return { data, success, error };
  }

  async updateTenant(id: string, body: ITenantUpdate) {
    const { data, success, error } = await this.put<string>(`/${id}`, body, {
      requestKey: "updateTenant",
    });
    return { data, success, error };
  }

  async getTenantRents(
    pageNumber: number,
    pageSize: number,
    searchTerm: string
  ) {
    const { data } = await this.get<ITenantRentListResponse>(
      "/rents",
      { pageNumber, pageSize, searchTerm },
      { requestKey: "getTenantRents" }
    );
    return data;
  }

  async updateTenantBaseRent(body: ITenantBaseRentUpdate) {
    const { data, success, error } = await this.put<string>("/rents", body, {
      requestKey: "updateTenantBaseRent",
    });
    return { data, success, error };
  }

  async updateTenantBaseRentBulk(body: ITenantBaseRentBulkUpdate) {
    const { data, success, error } = await this.put<string>("/rents/bulk", body, {
      requestKey: "updateTenantBaseRentBulk",
    });
    return { data, success, error };
  }

  async deleteTenantRent(id: string, rentId: string) {
    const { success, error } = await this.delete<string>(
      `/${id}/monthly/${rentId}`,
      {},
      { requestKey: "deleteTenantRent" }
    );
    return { success, error };
  }
}

export default new TenantService(apiGwClient, "/api/v1/tenants");
